/**
 * TanStack Start instance config (auto-discovered as the `start` entry — the
 * export MUST be named `startInstance`).
 *
 * `functionMiddleware` here runs on EVERY `createServerFn` automatically, so
 * cross-cutting policy lives in one place. `sanitizeErrors` guarantees no server
 * fn can leak internals to the client — see `src/lib/errors.ts` and the
 * "Client errors" note in AGENTS.md.
 *
 * IMPORTANT: defining a start instance TAKES OVER `requestMiddleware`, so the
 * built-in CSRF protection for server functions is no longer applied by default
 * — we must re-add it here (same filter Start uses by default) or same-origin
 * RPC endpoints would be exposed to cross-site requests.
 */
import {
  createCsrfMiddleware,
  createMiddleware,
  createStart,
} from '@tanstack/react-start'

import { stampAdClick } from './lib/analytics/attribution-server'
import { sanitizeErrors } from './lib/errors'
import { withSpan } from './lib/telemetry/telemetry'
import { apiFallbackResponse } from './routes/api/v1/-boundary'

const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === 'serverFn',
})

// Opens one span per server function, wrapping the whole fn (including
// sanitizeErrors below it) so DB spans + any captured exceptions inside the
// handler nest under it.
const tracingMiddleware = createMiddleware({ type: 'function' }).server(
  ({ next, serverFnMeta }) =>
    withSpan(`serverFn ${serverFnMeta?.name ?? 'unknown'}`, () => next(), {
      'serverfn.name': serverFnMeta?.name ?? 'unknown',
      'serverfn.filename': serverFnMeta?.filename ?? '',
    }),
)

// Errors are captured inside `toClientError` (see src/lib/errors.ts) rather
// than by a dedicated middleware — OTel's http instrumentation (see
// otel.server.ts) covers request tracing, so nothing here duplicates that.
// `tracingMiddleware` is listed first (outermost) so its span wraps
// `sanitizeErrors` and the handler itself.
// Captures Reddit's `rdt_cid` into a first-party cookie on the landing hit, so
// a signup days later can still be attributed. A no-op on any request without
// the parameter, which is all but the first one after an ad click.
const adClickMiddleware = createMiddleware({ type: 'request' }).server(
  ({ next, request }) => {
    stampAdClick(request)
    return next()
  },
)

// A request under /api/v1 that no route handler answered (unknown path, or a
// method the route does not define) would otherwise fall through to the SSR
// HTML page. Callers of a JSON API must never see HTML, so swap it for the
// API's own 404/405 envelope.
const apiBoundaryMiddleware = createMiddleware({ type: 'request' }).server(
  async ({ next, request }) => {
    const result = await next()
    const fallback = apiFallbackResponse(request, result.response)
    return fallback ? { ...result, response: fallback } : result
  },
)

export const startInstance = createStart(() => ({
  requestMiddleware: [csrfMiddleware, adClickMiddleware, apiBoundaryMiddleware],
  functionMiddleware: [tracingMiddleware, sanitizeErrors],
}))
